import { faAngleLeft, faAngleRight, faBars, faExclamationCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, log, styles } from "@repo/helpers";
import { Button, ButtonTypes, Col, Row, Spinner } from "@repo/ui";
import { Children, ComponentElement, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { scrollToPropertyName } from "..";
import { ButtonContainer, DEButton } from "../buttons";
import { ButtonContainerProps } from "../buttons/types";
import { ComponentProvider } from "../contexts/componentContext";
import { useDynamicSectionContext } from "../contexts/dynamicSectionContext";
import { useEditDataContext } from "../contexts/editDataContext";
import { useValidationContext } from "../contexts/validation";
import DynamicComponentContainer from "../dynamicComponentContainer/mobile";
import { useEnterJump } from "../helpers/enterJumpHook";
import MobileSectionList from "../mobileSectionList";
import { sectionType } from "../models/enums";
import { MappedSection } from "../models/mappedComponent";
import style from "./style.module.scss";
import { MobileViewProps, Props } from "./types";

export function MobileViewContainer(props: MobileViewProps) {
	const { validationResults, clearValidationTriggered, setValidationTriggered } = useValidationContext();
	const { sections, visibleSections } = useDynamicSectionContext();
	const {
		children,
		readOnly,
		menuActions,
		onHideList,
		customFunctions,
		onChangeSection,
		valueChanged,
		valuesChanged,
		setCopiedFrom,
		deleteUploadedFile,
		getUploadedFile,
		getCustomComponent,
		getComponentAddon,
		getComponentAddonPosition,
		locationTracker,
		alwaysShowPages,
		skipToNextPage,
		pendingEdits,
		...restProps
	} = props;

	const editDataContext = useEditDataContext();

	const names = useMemo(() => sections?.map((s) => s.linkName) ?? [], [sections]);
	const [activeIndex, setActiveIndex] = useState(0);
	const activeSection = useMemo(() => sections && sections[activeIndex], [sections, activeIndex]);
	const activePage = useMemo(() => names[activeIndex], [names, activeIndex]);
	const [showList, setShowList] = useState(false);
	const [waitingToGoNext, setWaitingToGoNext] = useState(false);
	const fieldToJumpTo = useRef<string>(null);
	const initialized = useRef(false);
	const visitedAllPages = useRef(false);

	useEffect(() => {
		if (skipToNextPage && !visibleSections.length) {
			console.log("No visible sections, moving to next page...");
			skipToNextPage();
		}
	}, [visibleSections, skipToNextPage]);

	useEffect(() => {
		if (!sections.length || !validationResults.any() || initialized.current) return;
		initialized.current = true;
		//If everything completed, jump to end on initialize
		if (!alwaysShowPages && !validationResults.filter((v) => hasValue(v.value)).any())
			setActiveIndex(sections.length - 1);
	}, [sections, alwaysShowPages]);

	useEffect(() => {
		onChangeSection && onChangeSection(activeSection);
		clearValidationTriggered();
		if (activeIndex >= sections.length - 1) visitedAllPages.current = true;
	}, [activeIndex]);

	useEffect(() => {
		tryScroll();
		//Render might be slow, so this timeout waits for the page to load, then tries to jump to property
		function tryScroll(iteration = 4) {
			if (iteration === 0) fieldToJumpTo.current = null;
			if (fieldToJumpTo.current) {
				if (scrollToPropertyName(fieldToJumpTo.current)) fieldToJumpTo.current = null;
				else setTimeout(() => tryScroll(iteration - 1), 100);
			}
		}
	}, [activePage]);

	useEffect(() => {
		if (!showList && onHideList) onHideList();
	}, [showList]);

	useEffect(() => {
		setShowList(props.showList);
	}, [props.showList]);

	const asyncValidateSection = useCallback(
		customFunctions &&
			// @ts-ignore
			(customFunctions(activeSection?.options?.asyncValidationMethod) as () => Promise<boolean>),
		[customFunctions, activeSection?.options?.asyncValidationMethod],
	);

	const canGoNextFn = useCallback(async () => {
		if (!asyncValidateSection) return true;
		//If async validation method to run, put in waiting state
		setWaitingToGoNext(true);
		try {
			return await asyncValidateSection();
		} catch (e) {
			return false;
		} finally {
			setWaitingToGoNext(false);
		}
	}, [asyncValidateSection]);

	// TODO: SS (Oct. 2025) This might be necessary for BYOD but it breaks how child sections work for ACA.
	//  Rework if we get BYOD back up and running.
	// const validSection = useCallback(
	// 	(section: MappedSection) =>
	// 		!section.childSections.map(validSection).some((v) => !v) &&
	// 		!section.components.filter((c) => validationResults.getError(c)?.length),
	// 	[validationResults],
	// );

	const validSection = useCallback(
		(section: MappedSection) => !section.components?.filter((c) => validationResults.getError(c))?.length,
		[validationResults],
	);

	const validChildSection = useCallback(
		function (section: MappedSection) {
			if (!section.childSections.length) return validSection(section);
			return section.childSections.every(validChildSection);
		},
		[validSection],
	);

	const sectionIsValid = useCallback(
		function (section: MappedSection) {
			if (!validationResults.getError(section)) return true;
			const isIteratedSection = section.section.type === sectionType.iteratedEditable;
			const sectionIsValid = validSection(section);
			const childSectionsAreValid = !isIteratedSection || section.childSections.every(validChildSection);
			return sectionIsValid && childSectionsAreValid;
		},
		[validationResults, validSection, validChildSection],
	);

	const tryChangeIndex = useCallback(
		async (index: number, canChangeFn?: () => Promise<boolean>) => {
			if (index < 0 || index > sections.length - 1) return;
			for (let i = 0; i < index; i++) {
				const section = sections[i];
				if (!sectionIsValid(section)) {
					setValidationTriggered();
					return;
				}
			}

			const canChange = !canChangeFn || (await canChangeFn());
			if (canChange) setActiveIndex(index);
		},
		[setActiveIndex, sectionIsValid, sections],
	);

	const completedSections = useMemo(() => {
		const newCompletedSections = [] as string[];
		for (const section of sections) {
			//If page is completed, want to be able to navigate to the next page
			//So we always go one page past the last error
			newCompletedSections.push(section.linkName);
			if (!sectionIsValid(section)) break;
		}
		return newCompletedSections;
	}, [sections, sectionIsValid]);

	const activeSectionIsValid = useMemo(
		() => activeSection && sectionIsValid(activeSection),
		[activeSection, sectionIsValid],
	);

	const goToField = useCallback(
		function (sectionName: string) {
			return function (event: MouseEvent) {
				const sectionNames = sections.map((s) => s.label);
				const index = sectionNames.indexOf(sectionName);
				if (index === -1) {
					log(`Could not find section ${sectionName}`);
					return;
				}
				setActiveIndex(index);
				const propertyName = event.currentTarget.getAttribute("data-field");
				if (propertyName) fieldToJumpTo.current = propertyName;
			};
		},
		[sections, setActiveIndex],
	);

	const summaryEditPageExists = useCallback(
		(sectionName: string) => {
			const sectionNames = sections.map((s) => s.label);
			const index = sectionNames.indexOf(sectionName);
			return index !== -1;
		},
		[sections],
	);

	const goBack = useCallback(() => {
		tryChangeIndex(activeIndex - 1);
	}, [activeIndex, tryChangeIndex]);

	const goNext = useCallback(() => {
		if (!alwaysShowPages && !validationResults.filter((v) => hasValue(v.value)).any() && visitedAllPages.current) {
			//If all completed, jump to end
			tryChangeIndex(sections.length - 1, canGoNextFn);
		} else tryChangeIndex(activeIndex + 1, canGoNextFn);
	}, [activeIndex, tryChangeIndex, validationResults, canGoNextFn, alwaysShowPages]);

	const jumpToPage = useCallback(
		(linkName: string) => {
			const index = names.indexOf(linkName);
			if (index < 0) return false;

			tryChangeIndex(index);
			return true;
		},
		[names, tryChangeIndex],
	);
	useEnterJump(goNext);

	const { containerClass } = props;
	const waitingToChangeIndex = waitingToGoNext; //Adding this excess property as a holding place for other waiting states
	const canGoNext = activeIndex + 1 < names.length;
	const canGoBack = activeIndex > 0;
	const nextButtonClassName = styles(!canGoNext && style.navButtonDisabled);
	const backButtonClassName = styles(!canGoBack && style.navButtonDisabled);
	const nextButtonColor = activeSectionIsValid ? "blue" : "gray";
	const backButtonColor = waitingToGoNext ? "gray" : "black";
	const nextButtonIcon = waitingToGoNext ? (
		<Spinner tag={"h1"} />
	) : (
		<FontAwesomeIcon icon={faAngleRight} size="3x" color={nextButtonColor} />
	);

	useEffect(() => {
		if (!canGoNext) clearValidationTriggered();
	}, [canGoNext]);

	const buttonContainer = useMemo(
		() =>
			Children.map(children, (c: ComponentElement<ButtonContainerProps, any>) => {
				if (c?.type?.name === ButtonContainer.name)
					return {
						...c,
						props: { ...c.props, hideAll: c.props.hideAll ?? readOnly, center: true },
					};
				return null;
			}).filter((a) => a),
		[children, readOnly],
	);

	return (
		<ComponentProvider
			valueChanged={editDataContext?.updateProperty ?? valueChanged}
			valuesChanged={editDataContext?.updateProperties ?? valuesChanged}
			setCopiedFrom={editDataContext?.toggleCopiedProperties ?? setCopiedFrom}
			deleteUploadedFile={deleteUploadedFile}
			getUploadedFile={getUploadedFile}
			getCustomComponent={getCustomComponent}
			getComponentAddon={getComponentAddon}
			getComponentAddonPosition={getComponentAddonPosition}
			customFunctions={customFunctions}
			locationTracker={locationTracker}
			pendingEdits={pendingEdits}
		>
			<MobileSectionList
				sections={visibleSections}
				shown={showList}
				jumpToSection={jumpToPage}
				completedSections={completedSections}
				activePage={activePage}
				closeList={() => setShowList(false)}
				menuActions={menuActions}
			/>
			<Col xs={12} className={styles(style.outerContainer, containerClass)} id={"dataEntryContainer"}>
				{renderShowListButton()}
				<Row type="section" id="dynamicComponents" className={style.innerContainerClass}>
					<Col xs={1} className={style.navButtonContainer}>
						<Button
							onClick={goBack}
							disabled={!canGoBack || waitingToChangeIndex}
							className={backButtonClassName}
						>
							<FontAwesomeIcon icon={faAngleLeft} size="3x" color={backButtonColor} />
						</Button>
					</Col>
					<Col xs={10}>
						<DynamicComponentContainer
							{...restProps}
							activeIndex={activeIndex}
							readOnly={props.readOnly || waitingToChangeIndex}
							scrollToField={goToField}
							summaryEditPageExists={summaryEditPageExists}
							indexChanged={tryChangeIndex}
						>
							{buttonContainer}
						</DynamicComponentContainer>
					</Col>
					<Col xs={1} className={style.navButtonContainer}>
						<Button
							onClick={goNext}
							disabled={!canGoNext || waitingToChangeIndex}
							className={nextButtonClassName}
						>
							{nextButtonIcon}
						</Button>
					</Col>
				</Row>
			</Col>
		</ComponentProvider>
	);

	function renderShowListButton() {
		//If onHideList is included, that means the prop is controlled
		if (onHideList) return null;
		return (
			<Button onClick={() => setShowList(true)} className={style.menu}>
				<FontAwesomeIcon icon={faBars} size="3x" />
			</Button>
		);
	}
}

export function DynamicEditorMobile(props: Props) {
	const { readOnly, completed, submitText, submit, cancel } = props;
	return (
		<MobileViewContainer {...props}>
			<ButtonContainer>{renderButtons()}</ButtonContainer>
		</MobileViewContainer>
	);

	function renderButtons() {
		return (
			<ButtonContainer completed={completed} hideAll={readOnly}>
				<DEButton onClick={submit} types={[ButtonTypes.Primary]} mustBeComplete>
					{(typeof submitText == "string" ? submitText : submitText()) || "Save"}
				</DEButton>
				{renderCancelBtn()}
			</ButtonContainer>
		);
	}

	function renderCancelBtn() {
		if (!cancel) return null;
		return <DEButton onClick={cancel}>Cancel</DEButton>;
	}
}
